import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabaseClient';
import { useServices } from '../../contexts/services/ServiceContext';
import NotificationBell from '../../components/admin/NotificationBell';
import MessageNotificationBell from '../../components/admin/MessageNotificationBell';

// Type definitions
interface ServiceCategory {
  id: string;
  title: string;
  description: string;
  image: string;
  title_es?: string;
  description_es?: string;
  created_at?: string;
}

interface Service {
  id: string;
  category_id: string;
  name: string;
  duration: string;
  price: string;
  discountedPrice?: string;
  description: string;
  name_es?: string;
  description_es?: string;
  created_at?: string;
  is_active?: boolean;
}

// Toast notification type
interface Toast {
  id: number;
  message: string;
  type: 'success' | 'error' | 'info';
}

// Toast component for non-intrusive notifications
const Toast = ({ toast, onClose }: { toast: Toast; onClose: () => void }) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, 3000);
    
    return () => clearTimeout(timer);
  }, [onClose]);
  
  const bgColor = toast.type === 'success' 
    ? 'bg-green-500' 
    : toast.type === 'error' 
      ? 'bg-red-500' 
      : 'bg-blue-500';
  
  return (
    <div className={`${bgColor} text-white px-4 py-3 rounded shadow-lg flex justify-between items-center`}>
      <span>{toast.message}</span>
      <button 
        onClick={onClose}
        className="ml-4 text-white focus:outline-none"
      >
        ×
      </button>
    </div>
  );
};

// Tag component for displaying tags
const Tag = ({ tag, onRemove }: { tag: string; onRemove: () => void }) => {
  return (
    <div className="bg-gray-100 rounded-full px-3 py-1 text-sm text-gray-700 mr-2 mb-2 flex items-center">
      {tag}
      <button 
        onClick={onRemove} 
        className="ml-2 text-gray-500 hover:text-gray-700"
      >
        ×
      </button>
    </div>
  );
};

const ContentManagement = () => {
  // Get ServiceContext to invalidate cache after updates
  const { refreshData: refreshServiceCache } = useServices();
  
  // State variables
  const [categories, setCategories] = useState<ServiceCategory[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [isServiceModalOpen, setIsServiceModalOpen] = useState(false);
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [currentService, setCurrentService] = useState<Service | null>(null);
  const [currentCategory, setCurrentCategory] = useState<ServiceCategory | null>(null);
  const [activeTab, setActiveTab] = useState('categories');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
  const [toasts, setToasts] = useState<Toast[]>([]);
  
  // Add toast notification
  const addToast = (message: string, type: 'success' | 'error' | 'info' = 'info') => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type }]);
  };
  
  // Remove toast notification
  const removeToast = (id: number) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  };

  // Fetch data from Supabase
  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        
        // Fetch service categories
        const { data: categoriesData, error: categoriesError } = await supabase
          .from('service_categories')
          .select('*')
          .order('created_at');
        
        if (categoriesError) {
          throw new Error(`Error fetching categories: ${categoriesError.message}`);
        }
        
        setCategories(categoriesData || []);
        
        // Fetch services
        const { data: servicesData, error: servicesError } = await supabase
          .from('services')
          .select('*')
          .order('created_at');
        
        if (servicesError) {
          throw new Error(`Error fetching services: ${servicesError.message}`);
        }
        
        setServices(servicesData || []);
        
        // Set the first category as selected by default
        if (categoriesData && categoriesData.length > 0) {
          setSelectedCategoryId(categoriesData[0].id);
        }
        
      } catch (err: any) {
        setError(err.message || 'An error occurred while fetching data');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchData();
  }, []);

  // Handler functions for Categories
  const handleAddCategory = () => {
    setCurrentCategory(null);
    setIsCategoryModalOpen(true);
  };
  
  const handleEditCategory = (category: ServiceCategory) => {
    setCurrentCategory(category);
    setIsCategoryModalOpen(true);
  };
  
  const handleDeleteCategory = async (categoryId: string) => {
    if (!window.confirm('Are you sure you want to delete this category? This will also delete all services in this category.')) {
      return;
    }
    
    try {
      setIsLoading(true);
      
      // First delete all services in this category (due to foreign key constraint)
      const { error: deleteServicesError } = await supabase
        .from('services')
        .delete()
        .match({ category_id: categoryId });
      
      if (deleteServicesError) {
        throw new Error(`Error deleting services: ${deleteServicesError.message}`);
      }
      
      // Then delete the category
      const { error: deleteCategoryError } = await supabase
        .from('service_categories')
        .delete()
        .match({ id: categoryId });
      
      if (deleteCategoryError) {
        throw new Error(`Error deleting category: ${deleteCategoryError.message}`);
      }
      
      // Update state
      setCategories(categories.filter(category => category.id !== categoryId));
      setServices(services.filter(service => service.category_id !== categoryId));
      
      // If the deleted category was selected, select another one
      if (selectedCategoryId === categoryId) {
        const nextCategory = categories.find(cat => cat.id !== categoryId);
        setSelectedCategoryId(nextCategory?.id || null);
      }
      
      // Show success toast
      addToast('Category deleted successfully', 'success');
      
    } catch (err: any) {
      // Show error toast instead of alert
      addToast(`Failed to delete category: ${err.message}`, 'error');
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleSaveCategory = async (categoryData: ServiceCategory) => {
    try {
      setIsLoading(true);
      
      if (currentCategory) {
        // Update existing category
        // Check if category exists before update
        const { data: checkData, error: checkError } = await supabase
          .from('service_categories')
          .select('id')
          .eq('id', currentCategory.id)
          .single();

        if (checkError) {
          throw new Error(`Category does not exist or cannot be accessed: ${checkError.message}`);
        }

        const updateData = {
          title: categoryData.title,
          description: categoryData.description,
          image: categoryData.image,
          title_es: categoryData.title_es || null,
          description_es: categoryData.description_es || null
        };

        const { data, error, status, statusText } = await supabase
          .from('service_categories')
          .update(updateData)
          .eq('id', currentCategory.id)
          .select();

        if (error) {
          throw new Error(`Error updating category: ${error.message} (Code: ${error.code})`);
        }

        if (!data || data.length === 0) {
          // Fetch the updated category to ensure it was updated
          const { data: refreshedData, error: refreshError } = await supabase
            .from('service_categories')
            .select('*')
            .eq('id', currentCategory.id)
            .single();

          if (refreshError) {
            throw new Error(`Error confirming category update: ${refreshError.message}`);
          }

          if (refreshedData) {
            // Update the categories state with the refreshed data
            setCategories(categories.map(category => 
              category.id === currentCategory.id ? refreshedData : category
            ));
          } else {
            throw new Error('Failed to confirm category update - category not found after update');
          }
        } else {
          // Update state with returned data
          setCategories(categories.map(category => 
            category.id === currentCategory.id ? data[0] : category
          ));
        }
        
        // Show toast notification instead of alert
        addToast('Category updated successfully!', 'success');
      } else {
        // Add new category
        const { data, error } = await supabase
          .from('service_categories')
          .insert([categoryData])
          .select();

        if (error) {
          throw error;
        }

        if (data && data.length > 0) {
          setCategories([...categories, data[0]]);
          if (!selectedCategoryId) {
            setSelectedCategoryId(data[0].id);
          }
          // Show toast notification instead of alert
          addToast('Category created successfully!', 'success');
        }
      }
      
      setIsCategoryModalOpen(false);
    } catch (err: any) {
      // Show error toast instead of alert
      addToast(`Failed to save category: ${err.message}`, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // Handler functions for Services
  const handleAddService = () => {
    setCurrentService(null);
    setIsServiceModalOpen(true);
  };
  
  const handleEditService = (service: Service) => {
    setCurrentService(service);
    setIsServiceModalOpen(true);
  };
  
  const handleDeleteService = async (serviceId: string) => {
    if (!window.confirm('Are you sure you want to delete this service?')) {
      return;
    }
    
    try {
      setIsLoading(true);
      
      const { error } = await supabase
        .from('services')
        .delete()
        .match({ id: serviceId });
      
      if (error) throw error;
      
      // Update state
      setServices(services.filter(service => service.id !== serviceId));
      
      // Show success toast
      addToast('Service deleted successfully', 'success');
      
      // Invalidate ServiceContext cache so Services page reflects deletion immediately
      await refreshServiceCache(true);
    } catch (err: any) {
      // Show error toast instead of alert
      addToast(`Failed to delete service: ${err.message}`, 'error');
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleToggleServiceActive = async (service: Service) => {
    const newStatus = service.is_active === false ? true : false;
    try {
      const { error } = await supabase
        .from('services')
        .update({ is_active: newStatus })
        .eq('id', service.id);

      if (error) throw error;

      setServices(services.map(s =>
        s.id === service.id ? { ...s, is_active: newStatus } : s
      ));

      addToast(
        newStatus ? 'Service activated successfully' : 'Service hidden successfully',
        'success'
      );

      await refreshServiceCache(true);
    } catch (err: any) {
      addToast(`Failed to update service status: ${err.message}`, 'error');
    }
  };

  const handleSaveService = async (serviceData: Service) => {
    try {
      setIsLoading(true);
      
      if (currentService) {
        // Update existing service
        // Check if service exists before update
        const { data: checkData, error: checkError } = await supabase
          .from('services')
          .select('id')
          .eq('id', currentService.id)
          .single();

        if (checkError) {
          throw new Error(`Service does not exist or cannot be accessed: ${checkError.message}`);
        }

        const updateData = {
          name: serviceData.name,
          category_id: serviceData.category_id,
          duration: serviceData.duration,
          price: serviceData.price,
          discountedPrice: serviceData.discountedPrice || null,
          description: serviceData.description,
          name_es: serviceData.name_es || null,
          description_es: serviceData.description_es || null
        };

        const { data, error, status, statusText } = await supabase
          .from('services')
          .update(updateData)
          .eq('id', currentService.id)
          .select();

        if (error) {
          throw new Error(`Error updating service: ${error.message} (Code: ${error.code})`);
        }

        if (!data || data.length === 0) {
          // Fetch the updated service to ensure it was updated
          const { data: refreshedData, error: refreshError } = await supabase
            .from('services')
            .select('*')
            .eq('id', currentService.id)
            .single();

          if (refreshError) {
            throw new Error(`Error confirming service update: ${refreshError.message}`);
          }

          if (refreshedData) {
            // Update the services state with the refreshed data
            setServices(services.map(service => 
              service.id === currentService.id ? refreshedData : service
            ));
          } else {
            throw new Error('Failed to confirm service update - service not found after update');
          }
        } else {
          // Update state with returned data
          setServices(services.map(service => 
            service.id === currentService.id ? data[0] : service
          ));
        }
        
        // Show toast notification instead of alert
        addToast('Service updated successfully!', 'success');
        
        // Invalidate ServiceContext cache so Services page shows updated data immediately
        await refreshServiceCache(true);
      } else {
        // Add new service
        const { data, error, status, statusText } = await supabase
          .from('services')
          .insert([serviceData])
          .select();

        if (error) {
          throw new Error(`Error creating service: ${error.message} (Code: ${error.code})`);
        }

        if (data && data.length > 0) {
          setServices([...services, data[0]]);
          // Show toast notification instead of alert
          addToast('Service created successfully!', 'success');
          
          // Invalidate ServiceContext cache so Services page shows new service immediately
          await refreshServiceCache(true);
        } else {
          throw new Error('Failed to create service - no data returned from insert operation');
        }
      }

      setIsServiceModalOpen(false);
    } catch (err: any) {
      // Show error toast instead of alert
      addToast(`Failed to save service: ${err.message}`, 'error');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Filter services based on selected category
  const filteredServices = selectedCategoryId 
    ? services.filter(service => service.category_id === selectedCategoryId)
    : services;
  
  // Loading state
  if (isLoading && categories.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="inline-block w-12 h-12 border-4 border-[#B2D8C5] border-t-[#232347] rounded-full animate-spin mb-4"></div>
          <p className="text-[#232347]">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 pt-2 relative">
      {/* Toast container */}
      <div className="fixed bottom-4 right-4 z-50 space-y-2">
        {toasts.map(toast => (
          <Toast 
            key={toast.id} 
            toast={toast} 
            onClose={() => removeToast(toast.id)} 
          />
        ))}
      </div>
    
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-semibold text-[#344854]">Content Management</h1>
        <div className="flex items-center gap-3">
          <MessageNotificationBell />
          <NotificationBell />
          {activeTab === 'categories' ? (
            <button
              className="bg-[#3A5E63] text-white px-4 py-2 rounded-md hover:bg-[#2a4548] flex items-center"
              onClick={handleAddCategory}
            >
              <span className="mr-2">+</span> Add Category
            </button>
          ) : (
            <button
              className="bg-[#3A5E63] text-white px-4 py-2 rounded-md hover:bg-[#2a4548] flex items-center"
              onClick={handleAddService}
            >
              <span className="mr-2">+</span> Add Service
            </button>
          )}
        </div>
      </div>
      
      <div className="bg-white rounded-lg shadow">
        <div className="flex border-b">
          <button
            className={`px-4 py-2 text-sm font-medium ${activeTab === 'categories' ? 'text-[#3A5E63] border-b-2 border-[#3A5E63]' : 'text-gray-500 hover:text-gray-700'}`}
            onClick={() => setActiveTab('categories')}
          >
            Service Categories
          </button>
          <button
            className={`px-4 py-2 text-sm font-medium ${activeTab === 'services' ? 'text-[#3A5E63] border-b-2 border-[#3A5E63]' : 'text-gray-500 hover:text-gray-700'}`}
            onClick={() => setActiveTab('services')}
          >
            Services
          </button>
        </div>
        
        {activeTab === 'categories' ? (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Image</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Services</th>
                <th scope="col" className="relative px-6 py-3"><span className="sr-only">Actions</span></th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {categories.map((category) => (
                <tr key={category.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">{category.id}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{category.title}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-500 truncate max-w-xs">{category.description}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {category.image ? (
                      <img 
                        src={category.image} 
                        alt={category.title} 
                        className="h-10 w-16 object-cover rounded"
                      />
                    ) : (
                      <div className="text-sm text-gray-500">No image</div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">
                      {services.filter(service => service.category_id === category.id).length} services
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => handleEditCategory(category)}
                      className="text-indigo-600 hover:text-indigo-900 mr-3"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeleteCategory(category.id)}
                      className="text-red-600 hover:text-red-900"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
              {categories.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-6 py-4 text-center text-sm text-gray-500">
                    No categories found. Add your first service category!
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        ) : (
          <div>
            <div className="px-6 py-4 border-b bg-gray-50">
              <label htmlFor="category-filter" className="block text-sm font-medium text-gray-700 mb-1">
                Filter by Category
              </label>
              <select
                id="category-filter"
                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                value={selectedCategoryId || ''}
                onChange={(e) => setSelectedCategoryId(e.target.value || null)}
              >
                <option value="">All Categories</option>
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.title}
                  </option>
                ))}
              </select>
            </div>
            
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Duration</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Price</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Discounted</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th scope="col" className="relative px-6 py-3"><span className="sr-only">Actions</span></th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredServices.map((service) => {
                  const category = categories.find(c => c.id === service.category_id);
                  const isActive = service.is_active !== false;
                  return (
                    <tr key={service.id} className={isActive ? 'hover:bg-gray-50' : 'bg-gray-50 opacity-60'}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{service.name}</div>
                        <div className="text-xs text-gray-500 mt-1 max-w-xs truncate">{service.description}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500">{category?.title || 'Unknown'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500">{service.duration}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{service.price}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {service.discountedPrice ? (
                          <div className="text-sm text-red-600 font-medium">{service.discountedPrice}</div>
                        ) : (
                          <div className="text-sm text-gray-500">—</div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          isActive
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {isActive ? 'Active' : 'Hidden'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => handleToggleServiceActive(service)}
                          className={`mr-3 ${
                            isActive
                              ? 'text-gray-600 hover:text-gray-900'
                              : 'text-green-600 hover:text-green-900'
                          }`}
                        >
                          {isActive ? 'Hide' : 'Activate'}
                        </button>
                        <button
                          onClick={() => handleEditService(service)}
                          className="text-indigo-600 hover:text-indigo-900 mr-3"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteService(service.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {filteredServices.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-6 py-4 text-center text-sm text-gray-500">
                      No services found. {categories.length > 0 ? 'Add your first service!' : 'Please add a category first.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
      
      {/* Category Modal */}
      {isCategoryModalOpen && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50 overflow-y-auto">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 my-8 max-h-[90vh] flex flex-col">
            <div className="px-6 py-4 border-b flex justify-between items-center flex-shrink-0">
              <h3 className="text-lg font-medium text-gray-900">
                {currentCategory ? 'Edit Service Category' : 'Add New Service Category'}
              </h3>
              <button
                className="text-gray-400 hover:text-gray-500"
                onClick={() => setIsCategoryModalOpen(false)}
              >
                ✕
              </button>
            </div>
            
            <div className="p-6 overflow-y-auto flex-1">
              <form className="space-y-4">
                <div>
                  <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
                    Category Title
                  </label>
                  <input
                    type="text"
                    id="title"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    defaultValue={currentCategory?.title || ''}
                  />
                </div>
                
                <div>
                  <label htmlFor="id" className="block text-sm font-medium text-gray-700 mb-1">
                    Category ID
                  </label>
                  <input
                    type="text"
                    id="id"
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 ${currentCategory ? 'bg-gray-100' : ''}`}
                    defaultValue={currentCategory?.id || ''}
                    disabled={!!currentCategory}
                    placeholder="e.g. massage, facial, specialty"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Used for identification and URL paths. Use lowercase letters and no spaces.
                  </p>
                </div>
                
                <div>
                  <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    id="description"
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    defaultValue={currentCategory?.description || ''}
                    placeholder="Enter a description..."
                  ></textarea>
                </div>
                
                <div>
                  <label htmlFor="image" className="block text-sm font-medium text-gray-700 mb-1">
                    Image URL
                  </label>
                  <input
                    type="text"
                    id="image"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    defaultValue={currentCategory?.image || ''}
                    placeholder="https://example.com/image.jpg"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Enter a valid image URL. For future: we'll add image upload functionality.
                  </p>
                </div>
                
                {currentCategory?.image && (
                  <div className="mt-2">
                    <p className="text-sm font-medium text-gray-700 mb-1">Current Image:</p>
                    <img 
                      src={currentCategory.image} 
                      alt={currentCategory.title} 
                      className="h-40 w-full object-cover rounded"
                    />
                  </div>
                )}
                
                {/* Spanish Translation Fields */}
                <div className="pt-4 mt-4 border-t border-gray-200">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                    🇪🇸 Spanish Translation
                  </h4>
                  
                  <div className="mb-4">
                    <label htmlFor="title_es" className="block text-sm font-medium text-gray-700 mb-1">
                      Spanish Title (Título en Español)
                    </label>
                    <input
                      type="text"
                      id="title_es"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      defaultValue={currentCategory?.title_es || ''}
                      placeholder="Optional - Leave empty to use serviceContent.json"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="description_es" className="block text-sm font-medium text-gray-700 mb-1">
                      Spanish Description (Descripción en Español)
                    </label>
                    <textarea
                      id="description_es"
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      defaultValue={currentCategory?.description_es || ''}
                      placeholder="Optional - Leave empty to use serviceContent.json"
                    ></textarea>
                  </div>
                </div>
              </form>
            </div>
            
            <div className="px-6 py-4 border-t bg-gray-50 flex justify-end flex-shrink-0">
              <button
                type="button"
                className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 mr-3"
                onClick={() => setIsCategoryModalOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-[#3A5E63] hover:bg-[#2a4548] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#3A5E63]"
                onClick={() => {
                  const titleEl = document.getElementById('title') as HTMLInputElement;
                  const idEl = document.getElementById('id') as HTMLInputElement;
                  const descriptionEl = document.getElementById('description') as HTMLTextAreaElement;
                  const imageEl = document.getElementById('image') as HTMLInputElement;
                  const titleEsEl = document.getElementById('title_es') as HTMLInputElement;
                  const descriptionEsEl = document.getElementById('description_es') as HTMLTextAreaElement;
                  
                  // Validation
                  if (!titleEl.value.trim()) {
                    alert('Please enter a category title');
                    titleEl.focus();
                    return;
                  }
                  
                  if (!currentCategory && !idEl.value.trim()) {
                    alert('Please enter a category ID');
                    idEl.focus();
                    return;
                  }
                  
                  // For new category, ensure ID follows proper format
                  if (!currentCategory) {
                    const idValue = idEl.value.trim();
                    
                    // Validate ID format (letters, numbers, hyphens, underscores only)
                    if (!/^[a-z0-9-_]+$/.test(idValue)) {
                      alert('Category ID must contain only lowercase letters, numbers, hyphens, or underscores. No spaces or special characters allowed.');
                      idEl.focus();
                      return;
                    }
                  }
                  
                  // Create category data object
                  const categoryData: ServiceCategory = {
                    id: currentCategory?.id || idEl.value.trim(),
                    title: titleEl.value.trim(),
                    description: descriptionEl.value.trim(),
                    image: imageEl.value.trim(),
                    title_es: titleEsEl.value.trim() || undefined,
                    description_es: descriptionEsEl.value.trim() || undefined
                  };
                  
                  // Save the category
                  handleSaveCategory(categoryData);
                }}
              >
                {currentCategory ? 'Update Category' : 'Create Category'}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Service Modal */}
      {isServiceModalOpen && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50 overflow-y-auto">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 my-8 max-h-[90vh] flex flex-col">
            <div className="px-6 py-4 border-b flex justify-between items-center flex-shrink-0">
              <h3 className="text-lg font-medium text-gray-900">
                {currentService ? 'Edit Service' : 'Add New Service'}
              </h3>
              <button
                className="text-gray-400 hover:text-gray-500"
                onClick={() => setIsServiceModalOpen(false)}
              >
                ✕
              </button>
            </div>
            
            <div className="p-6 overflow-y-auto flex-1">
              <form className="space-y-4">
                <div>
                  <label htmlFor="service-name" className="block text-sm font-medium text-gray-700 mb-1">
                    Service Name
                  </label>
                  <input
                    type="text"
                    id="service-name"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    defaultValue={currentService?.name || ''}
                  />
                </div>
                
                <div>
                  <label htmlFor="service-category" className="block text-sm font-medium text-gray-700 mb-1">
                    Category
                  </label>
                  <select
                    id="service-category"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    defaultValue={currentService?.category_id || (selectedCategoryId || '')}
                  >
                    <option value="" disabled>Select a category</option>
                    {categories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.title}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label htmlFor="service-duration" className="block text-sm font-medium text-gray-700 mb-1">
                      Duration
                    </label>
                    <input
                      type="text"
                      id="service-duration"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      defaultValue={currentService?.duration || ''}
                      placeholder="e.g. 60 min"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="service-price" className="block text-sm font-medium text-gray-700 mb-1">
                      Price
                    </label>
                    <input
                      type="text"
                      id="service-price"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      defaultValue={currentService?.price || ''}
                      placeholder="e.g. $120"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="service-discounted-price" className="block text-sm font-medium text-gray-700 mb-1">
                      Discounted Price (optional)
                    </label>
                    <input
                      type="text"
                      id="service-discounted-price"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      defaultValue={currentService?.discountedPrice || ''}
                      placeholder="e.g. $99"
                    />
                  </div>
                </div>
                
                <div>
                  <label htmlFor="service-description" className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    id="service-description"
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    defaultValue={currentService?.description || ''}
                    placeholder="Enter a description of the service..."
                  ></textarea>
                </div>
                
                {/* Spanish Translation Fields */}
                <div className="pt-4 mt-4 border-t border-gray-200">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                    🇪🇸 Spanish Translation
                  </h4>
                  
                  <div className="mb-4">
                    <label htmlFor="service-name-es" className="block text-sm font-medium text-gray-700 mb-1">
                      Spanish Name (Nombre en Español)
                    </label>
                    <input
                      type="text"
                      id="service-name-es"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      defaultValue={currentService?.name_es || ''}
                      placeholder="Optional - Leave empty to use serviceContent.json"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="service-description-es" className="block text-sm font-medium text-gray-700 mb-1">
                      Spanish Description (Descripción en Español)
                    </label>
                    <textarea
                      id="service-description-es"
                      rows={4}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      defaultValue={currentService?.description_es || ''}
                      placeholder="Optional - Leave empty to use serviceContent.json"
                    ></textarea>
                  </div>
                </div>
              </form>
            </div>
            
            <div className="px-6 py-4 border-t bg-gray-50 flex justify-end flex-shrink-0">
              <button
                type="button"
                className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 mr-3"
                onClick={() => setIsServiceModalOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-[#3A5E63] hover:bg-[#2a4548] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#3A5E63]"
                onClick={() => {
                  const nameEl = document.getElementById('service-name') as HTMLInputElement;
                  const categoryEl = document.getElementById('service-category') as HTMLSelectElement;
                  const durationEl = document.getElementById('service-duration') as HTMLInputElement;
                  const priceEl = document.getElementById('service-price') as HTMLInputElement;
                  const discountedPriceEl = document.getElementById('service-discounted-price') as HTMLInputElement;
                  const descriptionEl = document.getElementById('service-description') as HTMLTextAreaElement;
                  const nameEsEl = document.getElementById('service-name-es') as HTMLInputElement;
                  const descriptionEsEl = document.getElementById('service-description-es') as HTMLTextAreaElement;
                  
                  // Validation
                  if (!nameEl.value.trim()) {
                    alert('Please enter a service name');
                    nameEl.focus();
                    return;
                  }
                  
                  if (!categoryEl.value) {
                    alert('Please select a category');
                    categoryEl.focus();
                    return;
                  }
                  
                  if (!durationEl.value.trim()) {
                    alert('Please enter a duration');
                    durationEl.focus();
                    return;
                  }
                  
                  if (!priceEl.value.trim()) {
                    alert('Please enter a price');
                    priceEl.focus();
                    return;
                  }
                  
                  // Additional validation for discounted price
                  if (discountedPriceEl.value.trim() && parseFloat(discountedPriceEl.value) >= parseFloat(priceEl.value)) {
                    alert('Discounted price must be less than the regular price');
                    discountedPriceEl.focus();
                    return;
                  }
                  
                  // Create service data object
                  const serviceData: Service = {
                    id: currentService?.id || '', // ID will be generated by Supabase for new services
                    name: nameEl.value.trim(),
                    category_id: categoryEl.value,
                    duration: durationEl.value.trim(),
                    price: priceEl.value.trim(),
                    discountedPrice: discountedPriceEl.value.trim() || undefined,
                    description: descriptionEl.value.trim(),
                    name_es: nameEsEl.value.trim() || undefined,
                    description_es: descriptionEsEl.value.trim() || undefined,
                    is_active: currentService?.is_active ?? true
                  };
                  
                  // Save the service
                  handleSaveService(serviceData);
                }}
              >
                {currentService ? 'Update Service' : 'Create Service'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContentManagement;
